import { useReactiveVar } from '@apollo/client';
import type {
  IntrinsicSystemProps,
  MotionPropsFor,
  SystemProps,
} from '@hover/blueprint';
import {
  Center,
  forwardRef,
  motion,
  useLatestCallback,
  useMergeRefs,
} from '@hover/blueprint';
import type { Transition } from 'framer-motion';
import React, { useEffect, useRef } from 'react';

import type { AiOrSampleDisplayImage } from '../../types';
import { getImageId } from '../../types';

import { FullscreenImageExitBackground } from './FullscreenImageExitBackground';
import { latestFullscreenImage } from './context';
import { getFullscreenImageLayoutId, useFullscreenTransition } from './helpers';
import { useActiveFullscreenImageId } from './useActiveFullscreenImage';

type TransitionMap = Extract<Transition, { [k: string]: Transition }>;
type DivProps = IntrinsicSystemProps<'div'>;
type EnterFullscreenHandler = (element: HTMLDivElement) => void;

export interface FullscreenImageThumbnailProps
  extends MotionPropsFor<DivProps> {
  /**
   * Whether this thumbnail can enter fullscreen. This should only be set for
   * edge cases where a component that's usually set up to transition into the
   * fullscreen view shouldn't in a specific case. This disables the fullscreen
   * layout animation and disables the logic that hides the thumbnail when it's
   * the current fullscreen image.
   */
  canEnterFullscreen?: boolean;
  image: AiOrSampleDisplayImage | undefined;
  /**
   * Z-index of the image as it's transitioning back from the fullscreen state.
   * This ensures it appears over its siblings as it zooms out back to its
   * position in the list.
   */
  exitDepth?: SystemProps['zIndex'];
  /**
   * Additional transition configuration. Note that the `layout` transition is
   * defined by this component so you'll want to specify the properties you want
   * to transition in the configuration.
   *
   * @example
   * ```tsx
   * <FullscreenImageThumbnail transition={{ opacity: { duration: 0.1 } }} />
   * ```
   */
  transition?: TransitionMap;
  onEnterFullscreen?: EnterFullscreenHandler;
  children?: React.ReactNode;
  enableLayoutAnimation?: boolean;
}

const onEnterFullscreenDefault: EnterFullscreenHandler = element =>
  element.scrollIntoView({ block: 'nearest' });

const MotionCenter = motion(Center);

const FullscreenImageThumbnail = forwardRef<
  FullscreenImageThumbnailProps,
  'div'
>(
  (
    {
      canEnterFullscreen = true,
      children,
      image,
      exitDepth = 2,
      onEnterFullscreen: onEnterFullscreenProp = onEnterFullscreenDefault,
      transition,
      enableLayoutAnimation = true,
      ...props
    },
    externalRef,
  ) => {
    const latestImage = useReactiveVar(latestFullscreenImage);
    const fullscreenImageId = useActiveFullscreenImageId();

    const id = getImageId(image);
    const latestImageId = getImageId(latestImage);

    const isFullscreen = !!image && fullscreenImageId === id;
    const isLatestFullscreen =
      !!latestImageId && latestImageId === getImageId(image?.aiGeneratedImage);

    const scrollRef = useRef<HTMLDivElement>(null);
    const ref = useMergeRefs(externalRef, scrollRef);

    const onEnterFullscreen = useLatestCallback(onEnterFullscreenProp, [
      onEnterFullscreenProp,
    ]);

    useEffect(() => {
      const { current: element } = scrollRef;

      // When this thumnbail's image is active in the fullscreen view, invoke
      // the `onEnterFullscreen` callback. The default callback behavior is to
      // scroll the page to reveal this thumbnail. This ensures that as the user
      // pages through images in fullscreen, the list always displays the image
      // they were viewing upon exiting fullscreen.

      if (element && isLatestFullscreen) {
        onEnterFullscreen(element);
      }
    }, [isLatestFullscreen, onEnterFullscreen]);

    const layoutTransition = useFullscreenTransition();

    if (!canEnterFullscreen)
      return (
        <Center {...(props as DivProps)} ref={ref}>
          {children}
        </Center>
      );

    // When the image for a thumbnail is currently fullscreen, render a regular
    // element without a `layoutId` instead of a motion element so Framer knows to
    // transition from the thumbnail to the `CarouselImage` in fullscreen.
    return (
      <>
        {isFullscreen ? (
          <Center {...(props as DivProps)} ref={ref} />
        ) : (
          <MotionCenter
            data-testid="fullscreen-image-thumbnail"
            layoutId={
              enableLayoutAnimation && image
                ? getFullscreenImageLayoutId(image)
                : undefined
            }
            onLayoutAnimationComplete={() => {
              if (!isLatestFullscreen) return;

              latestFullscreenImage(null);
            }}
            position="relative"
            transition={{
              ...transition,
              // To prevent unwanted layout animations in lists, set the layout
              // transition duration to 0 unless animating back from fullscreen
              layout: isLatestFullscreen ? layoutTransition : { duration: 0 },
            }}
            zIndex={isLatestFullscreen ? exitDepth : undefined}
            {...props}
            ref={ref}
          >
            {children}
          </MotionCenter>
        )}
        <FullscreenImageExitBackground
          background="neutral.900"
          isVisible={isFullscreen && isLatestFullscreen}
          zIndex={1}
        />
      </>
    );
  },
);

export { FullscreenImageThumbnail };
